import axios from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'

const clearPortalSession = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
}

const handleUnauthorized = () => {
  clearPortalSession()
  if (router.currentRoute.value.path !== '/login') {
    router.replace('/login')
  }
}

// 创建 axios 实例
const service = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL,
  timeout: 15000, // 请求超时时间
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
  }
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 在请求发送之前做一些处理，例如添加token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['token'] = `${token}`
    }
    
    // 添加语言标识到请求头
    const locale = localStorage.getItem('locale') || 'zh'
    config.headers['Accept-Language'] = locale

    // silent 时写入请求头，便于响应/错误拦截器识别（与 config.silent 双通道）
    if (config.silent === true) {
      config.headers['X-Silent-Request'] = 'true'
    }

    // 如果是 POST/PUT 请求且 data 是对象，转换为 URL-encoded 格式
    if ((config.method === 'post' || config.method === 'put') && config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      // 检查 Content-Type 是否为 application/x-www-form-urlencoded
      const contentType = config.headers['Content-Type'] || config.headers['content-type']
      if (contentType && contentType.includes('application/x-www-form-urlencoded')) {
        // 将对象转换为 URL-encoded 字符串
        const params = new URLSearchParams()
        for (const key in config.data) {
          if (config.data[key] !== null && config.data[key] !== undefined) {
            params.append(key, config.data[key])
          }
        }
        config.data = params.toString()
      }
    }
    
    // 添加请求参数调试信息
    // console.log('=== 请求拦截器 ===')
    // console.log('请求URL:', config.url)
    // console.log('请求方法:', config.method)
    // console.log('请求参数 (params):', config.params)
    // console.log('请求数据 (data):', config.data)
    // console.log('完整配置:', config)
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    
    // 添加数据验证
    if (!res) {
      console.error('API响应数据为空')
      return Promise.reject(new Error('API响应数据为空'))
    }

    // 由调用方自行判断业务码（如微信支付需根据 code_url 与 code 分支提示）
    if (response.config.skipBusinessCodeCheck) {
      return res
    }
    
    // 根据自定义错误码判断请求是否成功
    const successCodes = [0, '0', 'Succeed', 'SUCCESS']
    if (successCodes.includes(res.code)) {
      return res
    } else if (res.code === 401) {
      handleUnauthorized()
      return res
    } else {
      // 处理业务错误
      const errorMsg = res.msg || res.message || '未知错误'
      const isSilent = response.config?.silent === true ||
        response.config?.headers?.['X-Silent-Request'] === 'true'
      if (!isSilent) {
        ElMessage.error(errorMsg)
      }
      return Promise.reject(new Error(errorMsg))
    }
  },
  error => {
    // 处理 HTTP 错误状态码
    let message = '请求失败'
    
    if (error.response) {
      // 服务器返回了错误状态码
      const status = error.response.status
      const data = error.response.data
      
      switch (status) {
        case 400:
          message = data?.message || '请求参数错误'
          break
        case 401:
          message = '未授权，请重新登录'
          handleUnauthorized()
          break
        case 403:
          message = '拒绝访问'
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 500:
          message = data?.message || '服务器内部错误'
          break
        default:
          message = data?.message || `请求失败 (${status})`
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      message = '网络错误，请检查网络连接'
    } else {
      // 请求配置出错
      message = error.message || '请求配置错误'
    }

    const isSilent = error.config?.silent === true ||
      error.config?.headers?.['X-Silent-Request'] === 'true'
    if (!isSilent) {
      ElMessage.error(message)
    }
    return Promise.reject(error)
  }
)

// 封装 GET 请求
export function get(url, params, config = {}) {
  const requestConfig = {
    url,
    method: 'get',
    params,
    ...config
  }
  if (config.silent !== undefined) {
    requestConfig.silent = config.silent
  }
  return service(requestConfig)
}

// 封装 POST 请求（第三项可传 params、headers、silent 等与 axios 兼容的配置）
export function post(url, data, config = {}) {
  const requestConfig = {
    url,
    method: 'post',
    data,
    ...config
  }
  if (config.silent !== undefined) {
    requestConfig.silent = config.silent
  }
  return service(requestConfig)
}

// 封装 PUT 请求
export function put(url, data) {
  return service({
    url,
    method: 'put',
    data
  })
}

// 封装 DELETE 请求
export function del(url, params) {
  return service({
    url,
    method: 'delete',
    params
  })
}

export default service
